import axios from "axios";
import { useEffect, useCallback, useState, useRef } from "react";
import { Card, Tab, Tabs } from "react-bootstrap";

import { useAuth } from "hooks/useAuth";
import { useInterval } from "hooks/useInterval";

import StageTrackerTable from "../StageTracker/StageTrackerTable";

import CCTLAvailableAMTable from "./CCTLAvailableAMTable";
import ProductsInEvalTable from "./ProductsInEvalTable";

export default function CCTLProductsModule({
  amProjects,
  refetch,
  setAlertInfo,
}) {
  const [projects, setProjects] = useState([]);
  const [inEvalProducts, setInEvalProducts] = useState([]);
  const [progressPoints, setProgressPoints] = useState([]);
  const [projpps, setProjpps] = useState([]);
  const evalTableRef = useRef();
  const stageTableRef = useRef();
  const earTableRef = useRef();
  const amTableRef = useRef();
  const { authToken } = useAuth();

  const fetchInEvalProducts = useCallback(() => {
    axios
      .get(
        `${import.meta.env.VITE_DJANGO_ENDPOINT}project/product/current_user_products/?status=Pre-Proposal,Proposed,Pre-Evaluation,In Progress,In Assurance Maintenance`,
        {
          withCredentials: true,
          headers: { Authorization: `Token ${authToken}` },
        },
      )
      .then((response) => setInEvalProducts(response.data))
      .catch((error) =>
        console.log("Unable to get current products: " + error),
      );
  }, []);

  const fetchProjects = useCallback(() => {
    axios
      .get(
        `${import.meta.env.VITE_DJANGO_ENDPOINT}project/product/stage_tracker_products/`,
        {
          withCredentials: true,
          headers: {
            Authorization: `Token ${authToken}`,
          },
        },
      )
      .then((response) => setProjects(response.data))
      .catch((error) => console.log(error));
  }, []);

  const fetchGroupedPP = useCallback(() => {
    axios
      .get(
        `${import.meta.env.VITE_DJANGO_ENDPOINT}project/progress/get_grouped_progress_points/`,
        {
          withCredentials: true,
          headers: { Authorization: `Token ${authToken}` },
        },
      )
      .then((response) => setProgressPoints(response.data))
      .catch((error) => console.log("Unable to get progress points: " + error));
  }, []);

  const fetchGroupedProtectionProfiles = useCallback(() => {
    axios
      .get(
        `${import.meta.env.VITE_DJANGO_ENDPOINT}project/protection-profiles/get_grouped_pp/`,
        {
          withCredentials: true,
          headers: { Authorization: `Token ${authToken}` },
        },
      )
      .then((response) => {
        setProjpps(response.data);
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  useEffect(() => {
    fetchInEvalProducts();
  }, []);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    fetchGroupedProtectionProfiles();
  }, []);

  useEffect(() => {
    fetchGroupedPP();
  }, []);

  useInterval(() => {
    fetchInEvalProducts();
    if (evalTableRef.current) evalTableRef.current.onQueryChange();
    fetchProjects();
    if (stageTableRef.current) stageTableRef.current.onQueryChange();
    if (earTableRef.current) earTableRef.current.onQueryChange();
    fetchGroupedProtectionProfiles();
    fetchGroupedPP();
    if (amTableRef.current) amTableRef.current.onQueryChange();
  }, 300000); //5 min

  const reloadTables = () => {
    refetch();
    fetchInEvalProducts();
    if (evalTableRef.current) evalTableRef.current.onQueryChange();
    fetchProjects();
    if (stageTableRef.current) stageTableRef.current.onQueryChange();
    if (earTableRef.current) earTableRef.current.onQueryChange();
    fetchGroupedProtectionProfiles();
    fetchGroupedPP();
    if (amTableRef.current) amTableRef.current.onQueryChange();
  };

  return (
    <Card>
      <Card.Body>
        <Tabs defaultActiveKey="my products in eval" className="mb-3">
          <Tab
            eventKey="my products in eval"
            title={
              <div className="d-flex align-items-center">
                Products Management List
                <h4 className="text-primary ms-2 my-0 fw-bold">
                  {inEvalProducts?.count}
                </h4>
              </div>
            }
          >
            <ProductsInEvalTable
              tableRef={evalTableRef}
              progressPoints={progressPoints}
            />
          </Tab>
          <Tab
            eventKey="assurance maintenance"
            title={
              <div className="d-flex align-items-center">
                Products Available for AM
                <h4 className="text-primary ms-2 my-0 fw-bold">
                  {amProjects?.length}
                </h4>
              </div>
            }
          >
            <CCTLAvailableAMTable
              tableRef={amTableRef}
              projpps={projpps}
              refetch={reloadTables}
              setAlertInfo={setAlertInfo}
            />
          </Tab>
          <Tab
            eventKey="projects"
            title={
              <div className="d-flex align-items-center">
                Project State Tracker
                <h4 className="text-primary ms-2 my-0 fw-bold">
                  {projects?.count}
                </h4>
              </div>
            }
          >
            <StageTrackerTable tableRef={stageTableRef} />
          </Tab>
        </Tabs>
      </Card.Body>
    </Card>
  );
}
